// widget_theme (Channel::WebWidget::WIDGET_THEME_KEYS) → a complete, render-ready theme plus the CSS
// variables the Tailwind tokens in ../tailwind.config.js resolve to.

export const FONT_FAMILIES = {
  system:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  Inter: '"Inter", system-ui, sans-serif',
  Manrope: '"Manrope", system-ui, sans-serif',
  'Plus Jakarta Sans': '"Plus Jakarta Sans", system-ui, sans-serif',
  'DM Sans': '"DM Sans", system-ui, sans-serif',
  Poppins: '"Poppins", system-ui, sans-serif',
  Nunito: '"Nunito", system-ui, sans-serif',
  Montserrat: '"Montserrat", system-ui, sans-serif',
};

export const PRESETS = {
  arkemis: {
    primary_color: '#1f2fd6',
    accent_color: '#7c8cff',
    header_style: 'gradient',
  },
  midnight: {
    primary_color: '#111827',
    accent_color: '#f59e0b',
    header_style: 'solid',
  },
  forest: {
    primary_color: '#0f766e',
    accent_color: '#a3e635',
    header_style: 'solid',
  },
  sunset: {
    primary_color: '#e11d48',
    accent_color: '#fb923c',
    header_style: 'gradient',
  },
  ocean: {
    primary_color: '#0369a1',
    accent_color: '#22d3ee',
    header_style: 'gradient',
  },
  minimal: {
    primary_color: '#18181b',
    accent_color: '#71717a',
    header_style: 'light',
  },
};

export const DEFAULT_THEME = {
  preset: 'arkemis',
  primary_color: '#1f2fd6',
  accent_color: '',
  header_style: 'solid',
  appearance: 'light',
  font_family: 'system',
  corner_radius: 18,
  launcher_style: 'pill',
  launcher_label: '',
  launcher_icon: 'chat',
  position: 'right',
  offset_x: 24,
  offset_y: 24,
  title: '',
  subtitle: '',
  teaser_text: '',
  teaser_delay: 6,
  start_message: '',
  input_placeholder: '',
  whatsapp_number: '',
  whatsapp_label: '',
};

const HEX = /^#[0-9a-f]{6}$/i;

const hexToRgb = hex =>
  [1, 3, 5].map(index => parseInt(hex.slice(index, index + 2), 16));

const rgbToken = rgb => rgb.join(' ');

const mix = (a, b, weight) =>
  a.map((c, i) => Math.round(c * (1 - weight) + b[i] * weight));

// WCAG relative luminance; picks whichever of ink/white reads better on the colour.
const luminance = rgb => {
  const [r, g, b] = rgb.map(c => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const contrastRatio = (a, b) => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
};

const WHITE = [255, 255, 255];
const INK = [17, 20, 28];

const readableOn = rgb =>
  contrastRatio(rgb, WHITE) >= contrastRatio(rgb, INK) ? WHITE : INK;

// A near-black brand colour (Midnight, Minimal) disappears against the dark palette: the visitor's
// bubbles and the tray keys would merge into the canvas. Lift it toward white until it separates.
const DARK_SURFACE = [24, 27, 34];
const liftForDark = rgb => {
  let lifted = rgb;
  for (
    let step = 1;
    contrastRatio(lifted, DARK_SURFACE) < 3 && step <= 10;
    step += 1
  ) {
    lifted = mix(rgb, WHITE, step * 0.08);
  }
  return lifted;
};

const clamp = (value, min, max, fallback) => {
  const number = Number(value);
  if (Number.isNaN(number)) return fallback;
  return Math.min(max, Math.max(min, number));
};

// Precedence: component defaults < inbox fields (widget_color, name, tagline) < widget_theme < per-embed overrides.
export const resolveTheme = (appearance = {}, overrides = {}) => {
  const stored = appearance.widget_theme || {};
  const merged = {
    ...DEFAULT_THEME,
    primary_color: appearance.widget_color || DEFAULT_THEME.primary_color,
    title: appearance.welcome_title || appearance.website_name || '',
    subtitle: appearance.welcome_tagline || '',
    ...Object.fromEntries(
      Object.entries({ ...stored, ...overrides }).filter(
        ([, value]) => value !== null && value !== undefined && value !== ''
      )
    ),
  };

  return {
    ...merged,
    primary_color: HEX.test(merged.primary_color)
      ? merged.primary_color
      : DEFAULT_THEME.primary_color,
    accent_color: HEX.test(merged.accent_color) ? merged.accent_color : '',
    corner_radius: clamp(
      merged.corner_radius,
      0,
      28,
      DEFAULT_THEME.corner_radius
    ),
    offset_x: clamp(merged.offset_x, 0, 120, DEFAULT_THEME.offset_x),
    offset_y: clamp(merged.offset_y, 0, 160, DEFAULT_THEME.offset_y),
    teaser_delay: clamp(
      merged.teaser_delay,
      0,
      120,
      DEFAULT_THEME.teaser_delay
    ),
    avatar_url: appearance.avatar_url || '',
  };
};

export const themeVariables = (theme, isDark) => {
  const brand = hexToRgb(theme.primary_color);
  const primary = isDark ? liftForDark(brand) : brand;
  const accent = theme.accent_color
    ? hexToRgb(theme.accent_color)
    : mix(primary, WHITE, 0.35);
  const base = isDark
    ? {
        canvas: mix([12, 14, 19], primary, 0.06),
        surface: DARK_SURFACE,
        ink: [236, 238, 243],
        muted: [150, 156, 170],
        line: [44, 48, 58],
      }
    : {
        canvas: mix([246, 247, 250], primary, 0.035),
        surface: WHITE,
        ink: INK,
        muted: [104, 112, 128],
        line: [228, 231, 237],
      };

  return {
    '--ark-primary': rgbToken(primary),
    '--ark-on-primary': rgbToken(readableOn(primary)),
    '--ark-accent': rgbToken(accent),
    '--ark-on-accent': rgbToken(readableOn(accent)),
    '--ark-canvas': rgbToken(base.canvas),
    '--ark-surface': rgbToken(base.surface),
    '--ark-ink': rgbToken(base.ink),
    '--ark-muted': rgbToken(base.muted),
    '--ark-line': rgbToken(base.line),
    '--ark-danger': isDark ? '248 113 113' : '220 38 38',
    '--ark-radius': `${theme.corner_radius}px`,
    '--ark-font': FONT_FAMILIES[theme.font_family] || FONT_FAMILIES.system,
  };
};

// Web fonts must be declared on the host document; @font-face inside a shadow root is ignored.
export const loadFont = family => {
  if (!family || family === 'system' || !FONT_FAMILIES[family]) return;
  const id = `arkemis-chat-font-${family.replace(/\s+/g, '-').toLowerCase()}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
};
